import React from 'react';
import { Button } from '@/components/ui/button';
import { CheckCircle, Users, Award } from 'lucide-react';

const Hero = () => {
    return (
        <section id="home" className="hero-pattern min-h-[90vh] flex items-center py-16 lg:py-24">
            <div className="section-container">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    {/* Left Content */}
                    <div className="space-y-8 animate-fade-in">
                        <div>
                            <p className="text-primary font-medium text-sm uppercase tracking-wider mb-4">
                                Welcome To Dhakad Enterprises
                            </p>
                            <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.55rem] lg:leading-[1.2] font-bold text-foreground leading-tight">
                                Welcome To{' '}
                                <span className="gradient-text">Dhakad</span>{' '}
                                Enterprises
                            </h1>
                        </div>
                        
                        <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
                            Welcome to Dhakad Enterprises, your one-stop destination for premium building materials. 
                            We supply a wide range of products including bricks, sand, aggregate, steel, stone, cement, 
                            and Dr. Fixit solutions. Our commitment to quality ensures that you receive only the best 
                            materials for your construction needs.
                        </p>

                        {/* Feature Boxes */}
                        <div className="grid sm:grid-cols-2 gap-6 pt-4">
                            <div className="flex items-start gap-4 group">
                                <div className="icon-box shrink-0 group-hover:scale-110 transition-transform duration-300">
                                    <Users className="w-6 h-6 text-primary" />
                                </div>
                                <div>
                                    <h3 className="font-display font-semibold text-foreground mb-1">Quality Materials</h3>
                                    <p className="text-sm text-muted-foreground">
                                        Providing top-grade bricks, cement, stone, and steel to ensure your building projects stand strong.
                                    </p>
                                </div>
                            </div>
                            
                            <div className="flex items-start gap-4 group">
                                <div className="icon-box shrink-0 group-hover:scale-110 transition-transform duration-300">
                                    <Award className="w-6 h-6 text-primary" />
                                </div>
                                <div>
                                    <h3 className="font-display font-semibold text-foreground mb-1">Reliable Service</h3>
                                    <p className="text-sm text-muted-foreground">
                                        Committed to delivering excellence with every order, ensuring timely and dependable supply.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* CTA Buttons */}
                        <div className="flex flex-wrap gap-4 pt-4">
                            <Button 
                                className="bg-primary hover:bg-primary-dark text-primary-foreground font-medium px-8 py-6 text-base"
                                onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
                            >
                                Contact Us
                            </Button>
                            <Button 
                                variant="outline"
                                className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-medium px-8 py-6 text-base"
                                onClick={() => document.querySelector('#products')?.scrollIntoView({ behavior: 'smooth' })}
                            >
                                Our Products
                            </Button>
                        </div>
                    </div>

                    {/* Right Content - Hero Image */}
                    <div className="relative animate-fade-in-right" style={{ animationDelay: '0.2s' }}>
                        {/* Background decorative elements */}
                        <div className="absolute -top-8 -right-8 w-64 h-64 bg-primary/5 rounded-full blur-3xl"></div>
                        <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-primary/10 rounded-full blur-2xl"></div>
                        
                        {/* Main image container */}
                        <div className="relative z-10">
                            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl">
                                <img 
                                    src="https://customer-assets.emergentagent.com/job_673bff6c-2308-4c32-b063-9be9a3a42c9a/artifacts/hgtcjgjc_WhatsApp%20Image%202024-01-05%20at%203.08.27%20PM%20%281%29.jpeg" 
                                    alt="Dhakad Enterprises Store"
                                    className="w-full h-[400px] lg:h-[500px] object-cover"
                                />
                                {/* Overlay gradient */}
                                <div className="absolute inset-0 bg-gradient-to-t from-foreground/20 to-transparent"></div>
                            </div>
                            
                            {/* Floating card */}
                            <div className="absolute -bottom-6 -left-6 bg-card p-4 rounded-xl shadow-lg border border-border animate-float">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                                        <CheckCircle className="w-6 h-6 text-primary" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-foreground">Trusted Quality</p>
                                        <p className="text-sm text-muted-foreground">Since 2015</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
